import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { functionsAsia } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useRef, useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useData } from "@/contexts/DataContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SaathiToggleCard } from "@/components/SaathiToggleCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Link, useLocation } from "wouter";
import {
  ChevronLeft,
  LogOut,
  BadgeCheck,
  Loader2,
  ExternalLink,
  Zap,
  User as UserIcon,
  Building2,
  MapPin,
  Phone,
  Globe,
  Mail,
  Sparkles,
  Crown,
  Bell,
  Upload,
  Moon,
  BookOpen,
  Printer,
  Trash2,
  ChevronRight,
  Users,
  FileText,
  History,
  ShieldCheck,
  Copy
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { BusinessProfile } from "@/lib/firebase";
import { effectiveTier, isPlanActive } from "@/lib/planHelpers";

// 🚨 PREMIUM COMPONENT: Swipe To Action Slider
const SwipeToSwitch = ({ onConfirm, text }: { onConfirm: () => void, text: string }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState(0);

  useEffect(() => {
    const updateBounds = () => {
      if (containerRef.current) {
        setBounds(containerRef.current.offsetWidth - 56);
      }
    };
    updateBounds();
    const timeout = setTimeout(updateBounds, 100);
    window.addEventListener('resize', updateBounds);
    return () => {
      window.removeEventListener('resize', updateBounds);
      clearTimeout(timeout);
    };
  }, []);

  const handleDragEnd = (event: any, info: any) => {
    if (isUnlocked) return;
    const width = containerRef.current?.offsetWidth || 300;
    if (info.offset.x > width * 0.4) {
      setIsUnlocked(true);
      onConfirm();
      setTimeout(() => setIsUnlocked(false), 1000);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-14 mt-4 bg-amber-50 dark:bg-amber-900/40 rounded-[14px] flex items-center justify-center overflow-hidden touch-none select-none shadow-sm border-2 border-[#8B1A1A]/5"
    >
      <div className="flex items-center gap-2 z-0 pl-6 opacity-90 pointer-events-none">
        <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-bold tracking-wide shimmer-text">
          {text}
        </span>
      </div>
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: bounds }}
        dragElastic={0.05}
        onDragEnd={handleDragEnd}
        animate={{ x: isUnlocked ? bounds : 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="absolute left-1 w-12 h-12 thumb-shine rounded-xl shadow-[0_2px_12px_rgba(245,158,11,0.4)] flex items-center justify-center cursor-grab active:cursor-grabbing z-20 border border-amber-300/50"
      >
        <ChevronRight className="w-6 h-6 text-[#8B1A1A] drop-shadow-sm pointer-events-none" />
      </motion.div>
    </div>
  );
};

export default function Settings() {
  const { user, logout, isLoggingOut } = useAuth();
  const [, setLocation] = useLocation();

  const {
    userMode,
    setUserMode,
    businessProfile,
    saveBusinessProfile,
    businessLogo,
    rewardPoints: dataPoints,
    products,
    rewardHistory: firestoreHistory,
    proModeExpiry,
    settingsLoaded,
    readNotificationIds,
    dismissedNotificationIds,
    requestAccountDeletion,
    openProUpgradeModal, // 🚨 BROUGHT IN THE GLOBAL TRIGGER
    plan,
  } = useData();

  const { toast } = useToast();

  // 🌟 App Version State
  const [appVersion, setAppVersion] = useState<string>("Loading...");

  useEffect(() => {
    const fetchAppVersion = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          const info = await App.getInfo();
          setAppVersion(info.version);
        } else {
          setAppVersion("Web App");
        }
      } catch (err) {
        setAppVersion("Unknown");
      }
    };
    fetchAppVersion();
  }, []);

  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isNotifDenied, setIsNotifDenied] = useState(false);

  const rewardHistory = useMemo(() => {
    return firestoreHistory
      .map((h) => ({ ...h, date: new Date(h.date) }))
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [firestoreHistory]);

  const hasUnreadAlerts = useMemo(() => {
    if (userMode !== "pro") return false;

    if (isNotifDenied) return true;

    const readIds = new Set<string>(readNotificationIds);
    const dismissedIds = new Set<string>(dismissedNotificationIds);
    return products.some((p) => {
      if (p.stock === undefined) return false;
      if (p.stock === 0) {
        const id = `oos-${p.id}-${p.stock}`;
        return !readIds.has(id) && !dismissedIds.has(id);
      }
      if (p.lowStockAlert && p.lowStockAlert > 0 && p.stock <= p.lowStockAlert) {
        const id = `low-${p.id}-${p.stock}`;
        return !readIds.has(id) && !dismissedIds.has(id);
      }
      return false;
    });
  }, [products, userMode, readNotificationIds, dismissedNotificationIds, isNotifDenied]);

  const totalRewardPoints = useMemo(() => {
    if (settingsLoaded) return dataPoints;
    return rewardHistory.reduce((acc, curr) => acc + curr.points, 0);
  }, [dataPoints, rewardHistory, settingsLoaded]);

  const [profileForm, setProfileForm] = useState<BusinessProfile>({
    ownerName: "",
    businessName: "",
    businessAddress: "",
    businessEmail: "",
    businessPhone: "",
    businessWebsite: "",
    isGstEnabled: false,
    gstInclusive: false,
    gstin: "",
    cin: "",
    fssai: "",
  });

  useEffect(() => {
    if (businessProfile) {
      setProfileForm(businessProfile);
    } else if (user) {
      setProfileForm((prev) => ({
        ...prev,
        ownerName: user.displayName || "",
        businessEmail: user.email || "",
      }));
    }
  }, [businessProfile, user]);

  useEffect(() => {
    const checkDeniedStatus = async () => {
      const dismissedDenied = localStorage.getItem("denied_notif_card_dismissed");
      if (dismissedDenied) return;

      if (typeof Notification !== "undefined" && Notification.permission === 'denied') {
        setIsNotifDenied(true);
        return;
      }

      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const status = await PushNotifications.checkPermissions();
          if (status.receive === 'denied' || status.receive === 'prompt-with-rationale') {
            setIsNotifDenied(true);
          }
        }
      } catch (e) {
        console.error("Failed to check push permissions", e);
      }
    };

    checkDeniedStatus();
  }, []);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (profileForm.isGstEnabled && profileForm.gstin && profileForm.gstin.length > 0) {
      if (!/^[A-Z0-9]{15}$/.test(profileForm.gstin)) {
        toast({ title: "Invalid GSTIN", description: "GSTIN must be exactly 15 alphanumeric characters.", variant: "destructive" });
        return;
      }
    }
    if (profileForm.isGstEnabled && profileForm.cin && profileForm.cin.length > 0) {
      if (!/^[A-Z0-9]{21}$/.test(profileForm.cin)) {
        toast({ title: "Invalid CIN", description: "CIN must be exactly 21 alphanumeric characters.", variant: "destructive" });
        return;
      }
    }
    if (profileForm.isGstEnabled && profileForm.fssai && profileForm.fssai.length > 0) {
      if (!/^\d{14}$/.test(profileForm.fssai)) {
        toast({ title: "Invalid FSSAI", description: "FSSAI licence number must be exactly 14 digits.", variant: "destructive" });
        return;
      }
    }
    setIsSavingProfile(true);
    try {
      const cleanWebsite = profileForm.businessWebsite
        ? profileForm.businessWebsite.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")
        : "";
      const updatedProfile = {
        ...profileForm,
        businessWebsite: cleanWebsite,
        businessEmail: businessProfile?.businessEmail || user?.email || profileForm.businessEmail,
        phoneVerified: user?.phoneNumber ? profileForm.businessPhone === user.phoneNumber : false,
      };
      await saveBusinessProfile(updatedProfile);
      toast({ title: "Profile updated successfully" });
      // setShowProfileEdit(false); // Used if in another view
    } catch (error: any) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const isLocked = !!businessProfile?.ownerName;

  const handleLockedFieldClick = () => {
    if (isLocked) {
      toast({ title: "Contact Support", description: "To edit these details, please contact support@dailyhisaab.com" });
    }
  };

  const hasActiveProValidity = proModeExpiry && new Date(proModeExpiry) > new Date();

  const getInitials = (name?: string | null) => {
    if (!name) return "B";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to logout", variant: "destructive" });
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
        .profile-card {
          width: 100%;
          min-height: 160px;
          position: relative;
          padding: 24px;
          background: radial-gradient(178.94% 106.41% at 26.42% 106.41%, #FFFBF2 0%, rgba(255, 255, 255, 0) 71.88%), #FFFFFF;
          box-shadow: 0px 40px 40px rgba(139, 26, 26, 0.05), 0px 10px 20px rgba(139, 26, 26, 0.05);
          border-radius: 32px;
          transition: all 0.4s cubic-bezier(0.15, 0.83, 0.66, 1);
          cursor: pointer;
          overflow: hidden;
        }

        .dark .profile-card {
          background: radial-gradient(178.94% 106.41% at 26.42% 106.41%, rgba(139, 26, 26, 0.15) 0%, rgba(26, 22, 20, 0) 71.88%), #1A1614;
          box-shadow: 0px 40px 40px rgba(0, 0, 0, 0.2), 0px 10px 20px rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255,255,255,0.05);
        }

        .profile-card:active {
          transform: scale(0.97);
        }

        .avatar-wrapper {
          position: relative;
          display: inline-flex;
          border-radius: 50%;
        }

        .avatar-animated-ring {
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: conic-gradient(from 0deg, transparent, #f59e0b, #8B1A1A, #f59e0b, transparent);
          z-index: 0;
        }

        .shimmer-text {
          background: linear-gradient(
            to right,
            #b45309 20%,
            #fbbf24 50%,
            #b45309 80%
          );
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
        }

        .dark .shimmer-text {
          background: linear-gradient(
            to right,
            #fbbf24 20%,
            #fef08a 50%,
            #fbbf24 80%
          );
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
        }
      `}} />

      <div className="bg-[#FFFBF2] dark:bg-[#1A1614] min-h-screen flex flex-col">
        {/* ── HEADER ── */}
        <div className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md px-4 py-3 border-b border-slate-200/50 dark:border-slate-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Logo size="md" />
            </div>
            <div className="flex items-center gap-2">
              <Link href="/alerts">
                <button className="relative w-10 h-10 rounded-full bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  <Bell className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  {hasUnreadAlerts && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />}
                </button>
              </Link>
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-5 space-y-5 pb-8 overflow-y-auto">

          {/* 1. PREMIUM PROFILE CARD */}
          <div className="profile-card" onClick={() => setLocation("/profile")}>
            <div className="relative z-20 flex flex-col h-full justify-between gap-6">

              <div className="flex items-start justify-between w-full">
                <div className="flex flex-col gap-1 w-[70%]">
                  <span className="font-black text-[20px] uppercase leading-tight text-[#8B1A1A] dark:text-white truncate drop-shadow-sm">
                    {profileForm.businessName || "Your Business"}
                  </span>
                  <span className="font-medium text-[12px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                    {profileForm.businessEmail || user?.email || "No email provided"}
                  </span>
                  <span className="font-bold text-[13px] text-slate-600 dark:text-slate-300 truncate">
                    {profileForm.businessPhone || user?.email || "Add Contact Details"}
                  </span>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (user?.uid) {
                        navigator.clipboard.writeText(user.uid);
                        toast({ title: "User ID copied!" });
                      }
                    }}
                    className="mt-2 flex items-center gap-1 w-max px-2.5 py-1 bg-white/50 dark:bg-black/20 rounded-md border border-black/5 dark:border-white/10 hover:bg-white/80 dark:hover:bg-black/40 transition-colors backdrop-blur-sm"
                  >
                    <span className="font-bold text-[10px] text-slate-700 dark:text-slate-300 uppercase tracking-wider">ID: {user?.uid ? user.uid.slice(0, 8) + '...' : "Loading..."}</span>
                    <Copy className="w-3 h-3 text-slate-500" />
                  </div>
                </div>

                {/* Avatar with Spinning Glow (Read-Only) */}
                <div className="relative shrink-0 avatar-wrapper">
                  <div className="avatar-animated-ring" />
                  <Avatar className="relative z-10 h-16 w-16 border-[3px] border-[#FFFBF2] dark:border-slate-900 shadow-md bg-white dark:bg-slate-900">
                    <AvatarImage src={businessLogo || undefined} className="object-cover" />
                    <AvatarFallback className="text-xl bg-white dark:bg-slate-800 text-[#8B1A1A] dark:text-white font-black">
                      {getInitials(profileForm.businessName)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>

              {/* Bottom Row: Points & Status Pill */}
              <div className="flex items-end justify-between w-full mt-2">
                {/* 🌟 Attractive Points Display */}
                <div className="bg-gradient-to-r from-amber-100/80 to-orange-50/80 dark:from-amber-900/40 dark:to-orange-900/30 backdrop-blur-md px-4 py-2 rounded-xl flex items-center justify-center border border-amber-200/60 dark:border-amber-700/50 shadow-sm">
                  <span className="font-black text-[14px] text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                    <Zap className="w-4 h-4 fill-amber-500 text-amber-500" />
                    {totalRewardPoints.toLocaleString()} <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600/80 dark:text-amber-400/70 ml-0.5 pt-0.5">Pts</span>
                  </span>
                </div>

                {/* Status Pill — only active subscribers carry the Premium badge; free users show no mode label */}
                {hasActiveProValidity && (
                  <div className="bg-white/60 dark:bg-black/40 backdrop-blur-md px-4 py-2 rounded-xl flex items-center justify-center border border-white/50 dark:border-white/10 shadow-sm">
                    <span className="font-extrabold text-[12px] uppercase tracking-widest text-[#8B1A1A] dark:text-white flex items-center gap-1.5">
                      <Crown className="w-4 h-4 text-amber-600 dark:text-amber-500" /> Premium
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2. AD-FREE STATUS (active subscriber) / UPSELL (free user) */}
          {hasActiveProValidity ? (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-3xl p-5 shadow-sm border border-amber-200 dark:border-amber-800 relative overflow-hidden">
              <Sparkles className="absolute top-0 right-0 p-4 h-20 w-20 text-amber-500/10 -rotate-12" />
              <div className="flex items-center justify-between mb-4 relative z-10">
                <div>
                  <p className="text-[10px] font-black text-amber-700 dark:text-amber-500 uppercase tracking-widest mb-1">Premium Subscription</p>
                  <h3 className="font-bold text-lg text-amber-900 dark:text-amber-400 flex items-center gap-2">Active</h3>
                </div>
                <div className="h-10 w-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center border border-amber-200 dark:border-amber-700">
                  <Crown className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <div className="flex items-center justify-between bg-white/60 dark:bg-slate-900/40 rounded-2xl p-3 backdrop-blur-sm border border-white/50 dark:border-slate-800/50 relative z-10">
                <div>
                  <p className="text-xs text-amber-800 dark:text-amber-300/80 font-medium">Valid until</p>
                  <p className="text-sm font-bold text-amber-900 dark:text-amber-400">
                    {proModeExpiry ? new Date(proModeExpiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}
                  </p>
                </div>
                <button
                  onClick={() => setLocation("/billing")}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-[11px] uppercase tracking-widest font-black rounded-xl transition-transform active:scale-95 shadow-sm"
                >
                  Manage Billing
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={openProUpgradeModal}
              className="w-full text-left bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-3xl p-5 shadow-sm border border-amber-200 dark:border-amber-800 relative overflow-hidden active:scale-[0.99] transition-transform"
            >
              <Sparkles className="absolute top-0 right-0 p-4 h-20 w-20 text-amber-500/10 -rotate-12" />
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-[10px] font-black text-amber-700 dark:text-amber-500 uppercase tracking-widest mb-1">Go Premium</p>
                  <h3 className="font-bold text-lg text-amber-900 dark:text-amber-400">Remove all ads</h3>
                  <p className="text-xs text-amber-800 dark:text-amber-300/80 font-medium mt-1">Subscribe for a clean, distraction-free experience.</p>
                </div>
                <div className="h-10 w-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center border border-amber-200 dark:border-amber-700">
                  <Crown className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </button>
          )}

          {/* 2.5 ULTIMATE — DESKTOP ACCESS (gated by Ultimate tier) */}
          {plan?.tier === "ultimate" && plan?.expiresAt && new Date(plan.expiresAt) > new Date() && (
            <Link href="/ultimate-admin">
              <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 p-4 flex items-center gap-4 active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                  <Crown className="h-5 w-5 text-indigo-500" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-slate-900 dark:text-white">Manage Desktop Access</p>
                  <p className="text-xs text-slate-500 font-medium">Invite staff, revoke devices, control permissions</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            </Link>
          )}

          {/* 3. REWARDS & GROWTH GROUP */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
            <Link href="/rewards">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-amber-500 fill-amber-500" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">
                      {plan?.tier === "ultimate" ? "DailyHisaab Store" : "Reward Points"}
                    </p>
                    <p className="text-xs text-slate-500 font-medium">
                      {plan?.tier === "ultimate" ? "Shop thermal printer, paper & more" : "Redeem points for Premium time"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </div>
              </div>
            </Link>

            {plan?.tier !== "ultimate" && (
            <Link href="/refer">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">Refer & Earn</p>
                    <p className="text-xs text-slate-500 font-medium">Invite friends, get 500 points</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Link>
            )}
          </div>

          {/* 4. PRO BUSINESS TOOLS GROUP */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
            <Link href="/printer-settings">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/30 flex items-center justify-center">
                    <Printer className="h-5 w-5 text-[#8B2121] dark:text-red-400" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">Printer Setup</p>
                    <p className="text-xs text-slate-500 font-medium">Bluetooth & USB devices</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Link>

            <Link href="/invoice-settings">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">Invoice Settings</p>
                    <p className="text-xs text-slate-500 font-medium">Terms, footers, & GST</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Link>

            <Link href="/my-reports">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center">
                    <History className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">Report History</p>
                    <p className="text-xs text-slate-500 font-medium">View and download past reports</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Link>

            <Link href="/gst-reports">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm text-slate-900 dark:text-white">GST Filing</p>
                      {(() => {
                        // Show plan-tier badge (Pro / Ultimate) only when user actually has the feature.
                        // Basic users see a muted Pro badge; the GstReports page itself shows the upsell.
                        const tier = effectiveTier(plan);
                        const active = isPlanActive(plan);
                        if (tier === "ultimate" && active) {
                          return <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#8B6914] border border-[#D4AF37]/40">Ultimate</span>;
                        }
                        if (tier === "pro" && active) {
                          return <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Premium</span>;
                        }
                        return <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Premium</span>;
                      })()}
                    </div>
                    <p className="text-xs text-slate-500 font-medium">GSTR-1 + GSTR-3B for your CA</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Link>
          </div>

          {/* 5. PREFERENCES GROUP */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center border border-slate-100 dark:border-slate-700">
                  <Moon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <p className="font-bold text-sm text-slate-900 dark:text-white">Appearance</p>
                  <p className="text-xs text-slate-500 font-medium">Light · Dark · Follow System</p>
                </div>
              </div>
              <ThemeToggle />
            </div>
          </div>

          {/* 5b. SAATHI AI TOGGLE */}
          <SaathiToggleCard />

          {/* 6. SUPPORT GROUP */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
            <Link href="/guide">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">User Guide</p>
                    <p className="text-xs text-slate-500 font-medium">Learn how to use the app</p>
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-slate-300" />
              </div>
            </Link>

            <Link href="/support">
              <div className="p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">Need Help?</p>
                    <p className="text-xs text-slate-500 font-medium">Contact our support team</p>
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-slate-300" />
              </div>
            </Link>
          </div>

          {/* 7. DANGER ZONE */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-red-100 dark:border-red-900/30 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="w-full p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors text-left" disabled={isLoggingOut}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                      <LogOut className="h-5 w-5 text-red-600" />
                    </div>
                    <p className="font-bold text-sm text-red-600 dark:text-red-400">{isLoggingOut ? "Logging out..." : "Sign Out"}</p>
                  </div>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
                <div className="p-8 pb-6 text-center">
                  <div className="w-16 h-16 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <LogOut className="h-8 w-8 text-[#8B2121] dark:text-red-400" />
                  </div>
                  <AlertDialogTitle className="text-2xl font-bold text-[#8B2121] dark:text-red-400 mb-3 tracking-tight">Log Out?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed px-2">
                    Are you sure you want to <strong>log out</strong> of your account?
                  </AlertDialogDescription>
                </div>
                <div className="px-6 pb-6 flex flex-col gap-3">
                  <AlertDialogAction onClick={handleLogout} className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20" style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}>
                    Log Out
                  </AlertDialogAction>
                  <AlertDialogCancel className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent mt-0">
                    Cancel
                  </AlertDialogCancel>
                </div>
              </AlertDialogContent>
            </AlertDialog>

            <button onClick={() => setShowDeleteWarning(true)} className="w-full p-4 flex items-center justify-between active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors text-left opacity-70 hover:opacity-100">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-slate-400 dark:text-slate-500" />
                </div>
                <p className="font-bold text-sm text-slate-500 dark:text-slate-400">Delete Account</p>
              </div>
            </button>
          </div>

          {/* 🌟 App Version Display */}
          <div className="pt-2 pb-0 flex flex-col items-center justify-center opacity-50 pointer-events-none select-none">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
              Version {appVersion}
            </p>
          </div>

          {/* Delete Account Warning Dialog */}
          <AlertDialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
            <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm p-0 overflow-hidden">
              <div className="p-8 pb-6 text-center">
                <div className="w-16 h-16 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trash2 className="h-8 w-8 text-[#8B2121] dark:text-red-400" />
                </div>
                <AlertDialogTitle className="text-2xl font-bold text-[#8B2121] dark:text-red-400 mb-3 tracking-tight">Delete Account?</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed px-2">
                  Your account will be permanently deleted <strong>within 30 days</strong>. You can cancel this request at any time by logging back in.
                </AlertDialogDescription>
              </div>
              <div className="px-6 pb-6 flex flex-col gap-3">
                <AlertDialogAction onClick={() => { setShowDeleteWarning(false); setShowDeleteForm(true); }} className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20" style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}>
                  Yes, Request Deletion
                </AlertDialogAction>
                <AlertDialogCancel className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent mt-0">
                  Cancel
                </AlertDialogCancel>
              </div>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete Account Reason Form */}
          <Dialog open={showDeleteForm} onOpenChange={(o) => { if (!isSubmittingDelete) setShowDeleteForm(o); }}>
            <DialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm p-0 overflow-hidden">
              <div className="p-8 pb-4">
                <div className="w-14 h-14 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mb-5">
                  <Trash2 className="h-7 w-7 text-[#8B2121] dark:text-red-400" />
                </div>
                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white mb-1">Request Account Deletion</DialogTitle>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Help us understand why you're leaving. This is optional.</p>
                <textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Please tell us why you want to delete your account (optional)" rows={4} className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-4 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#8B2121]/30" />
              </div>
              <div className="px-8 pb-8 flex flex-col gap-3">
                <button
                  disabled={isSubmittingDelete}
                  onClick={async () => {
                    setIsSubmittingDelete(true);
                    try {
                      await requestAccountDeletion(deleteReason);
                      try {
                        const name = businessProfile?.ownerName || businessProfile?.businessName || "Valued Customer";
                        const email = businessProfile?.businessEmail || user?.email || user?.phoneNumber || "no-email-provided";
                        const sendDeletion = httpsCallable(functionsAsia, 'triggerDeletionRequestEmail');
                        await sendDeletion({ name, email, reason: deleteReason });
                      } catch (emailErr) {
                        console.warn("Failed to trigger deletion email, but DB updated:", emailErr);
                      }
                      setShowDeleteForm(false);
                      await handleLogout();
                    } catch (err) {
                      console.error("Deletion request error:", err);
                      toast({ title: "Something went wrong", description: "Please try again or contact support." });
                    } finally {
                      setIsSubmittingDelete(false);
                    }
                  }}
                  className="w-full py-4 rounded-xl font-semibold text-[16px] text-white shadow-lg shadow-red-900/20 disabled:opacity-60 transition-all"
                  style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
                >
                  {isSubmittingDelete ? "Submitting..." : "Submit & Log Out"}
                </button>
                <button disabled={isSubmittingDelete} onClick={() => setShowDeleteForm(false)} className="w-full py-4 rounded-xl font-semibold text-[16px] text-[#8B2121] dark:text-red-400 border-2 border-[#8B2121]/10 dark:border-white/10 bg-transparent">
                  Cancel
                </button>
              </div>
            </DialogContent>
          </Dialog>

        </div>
      </div>
    </>
  );
}